import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {CircularProgress, createFilterOptions, Tooltip} from "@mui/material";
import {Row} from "reactstrap";
import {OutlinedAutoComplete} from "../../../../../../../components/base/auto_complete/outlined_auto_complete";
import {OutlinedInput} from "../../../../../../../components/base/input/outlined_input";
import Api from "../../../../../../../../core/services/api/api";
import DateUtils from "../../../../../../../../core/services/date-utils";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Chips, {ChipsTypes} from "../../../../../../../components/base/chips/chips";
import {dataGridCellTextProps} from "../../../../../../../../packages/data-grid";
import SaveOrRollback from "../../../../../../../components/app-specific/save-or-rollback";
import {useTranslation} from "react-i18next";
import {useTransferOperators} from "../use-transfer-operators";

const filter = createFilterOptions();

const TransferItemCommitEditor = ({
                                      item,
                                      transferId,
                                      warehouseCode,
                                      readonly,
                                      onItemUpdated,
                                      sourceAvailability,
                                      pending,
                                  }) => {
    const {t} = useTranslation('views', {keyPrefix: 'inventory-transfer.batch-information.items'});
    const isMounted = useIsMounted();
    const {getOperatorName, resolveOperator} = useTransferOperators();
    const [mode, setMode] = useState('unknown');
    const [autoCompleteText, setAutoCompleteText] = useState('');
    const [autoCompleteValue, setAutoCompleteValue] = useState();
    const [autoCompleteSerials, setAutoCompleteSerials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [qtyValue, setQtyValue] = useState(item?.totalScannedCount ?? 0);
    const [qtyCommitting, setQtyCommitting] = useState(false);

    const committedSerialNumbers = useMemo(
        () => item?.committedSerials?.map((serial) => serial.serialNumber) ?? [],
        [item?.committedSerials],
    );

    /**
     * Resolves whether this line uses serial or quantity commit UI.
     */
    const resolveCommitMode = useCallback(async () => {
        if (!warehouseCode || !item?.partNo) {
            setMode('quantity');
            return;
        }
        setLoading(true);
        const response = await Api.getInventorySerialNumbers(warehouseCode, item.partNo, false);
        if (!isMounted()) {
            return;
        }
        setLoading(false);
        if (response?.resultFlag) {
            const serials = response.data ?? [];
            setAutoCompleteSerials(serials);
            setMode(serials.length > 0 || committedSerialNumbers.length > 0 ? 'serial' : 'quantity');
            return;
        }
        setMode(committedSerialNumbers.length > 0 ? 'serial' : 'quantity');
    }, [committedSerialNumbers.length, isMounted, item?.partNo, warehouseCode]);

    /**
     * Resolves commit mode on mount so the editor is visible without requiring a click.
     */
    useEffect(() => {
        if (readonly) {
            return;
        }
        setMode('unknown');
        void resolveCommitMode();
    }, [item?.id, item?.partNo, readonly, resolveCommitMode, warehouseCode]);

    /**
     * Keeps the quantity editor in sync with the latest item state.
     */
    useLayoutEffect(() => {
        setQtyValue(item?.totalScannedCount ?? 0);
    }, [item?.totalScannedCount]);

    /**
     * Applies an item update returned from commit/uncommit APIs.
     */
    const applyItemUpdate = useCallback((apiItem) => {
        if (!apiItem) {
            return;
        }
        onItemUpdated?.(apiItem);
    }, [onItemUpdated]);

    /**
     * Commits or uncommits a serial number on this transfer line.
     */
    const toggleSerialNumber = useCallback(async (serialNumber, isAdding) => {
        setCommitting(true);
        const payload = {
            transferId,
            itemId: item.id,
            committedQty: 1,
            serialNumbers: [serialNumber],
        };
        const response = isAdding
            ? await Api.commitInventoryTransferItem(payload)
            : await Api.uncommitInventoryTransferItem(payload);
        if (!isMounted()) {
            return;
        }
        setCommitting(false);
        if (response?.resultFlag) {
            applyItemUpdate(response.data);
        }
    }, [applyItemUpdate, isMounted, item.id, transferId]);

    /**
     * Handles selecting a serial number from the autocomplete.
     */
    const onSelectNewSerialNumber = useCallback((_, serialNumber) => {
        if (!serialNumber) {
            serialNumber = '';
        }
        if (typeof serialNumber === 'string') {
            setAutoCompleteText(serialNumber);
            return;
        }
        if (typeof serialNumber === 'object') {
            if (!serialNumber.value?.isAvailable) {
                setAutoCompleteValue(null);
                setAutoCompleteText('');
                return;
            }
            void toggleSerialNumber(serialNumber.value.serialNumber, true);
            setAutoCompleteValue(null);
            setAutoCompleteText('');
        }
    }, [toggleSerialNumber]);

    /**
     * Filters autocomplete options to hide already committed serial numbers.
     */
    const filterOptions = useCallback((options, params) => {
        const filtered = filter(options, params);
        return filtered.filter((option) => !committedSerialNumbers.includes(option.value?.serialNumber));
    }, [committedSerialNumbers]);

    /**
     * Renders a serial number option in the autocomplete dropdown.
     */
    const renderAutoCompleteSerial = useCallback((props, option) => {
        if (!option) {
            return '';
        }
        if (typeof option === 'string') {
            return <p className={'mx-2 p-2'}>{option}</p>;
        }
        if (option?.title === 'totalCount') {
            return (
                <Row className={'no-gutters align-items-center w-100 justify-content-between py-2'}>
                    <p className={'text-left text-sm secondary-color-1 font-weight-bold mr-3'}>
                        {t('totalCount')}
                    </p>
                    <p className={'text-left text-sm secondary-color-1 font-weight-bold text-wrap'}>
                        {option.value}
                    </p>
                </Row>
            );
        }
        return (
            <Row className={'no-gutters align-items-center w-100 justify-content-between py-2'}>
                <p className={'text-left text-sm text-color-1 mr-3'}>
                    {option.value?.serialNumber}
                </p>
                <p className={'text-left text-sm text-color-3 text-wrap'}>
                    WHSE: {option.value?.warehouseCode}
                </p>
            </Row>
        );
    }, [t]);

    const serialOptions = useMemo(() => [
        ...(loading ? [] : [{
            title: 'totalCount',
            value: autoCompleteSerials?.filter((serial) => serial.isAvailable)?.length ?? 0,
            disabled: true,
        }]),
        ...(autoCompleteSerials?.map((serial) => ({
            title: serial.serialNumber,
            value: serial,
        })) ?? []),
    ], [autoCompleteSerials, loading]);

    const serialChips = useMemo(() => {
        const chipsType = readonly
            ? ChipsTypes.none
            : committing
                ? ChipsTypes.loading
                : ChipsTypes.remove;

        return (item?.committedSerials ?? []).map((serial) => {
            const chip = (
                <span key={serial.serialNumber} {...dataGridCellTextProps}>
                    <Chips
                        type={chipsType}
                        content={serial.serialNumber}
                        onIconClick={() => !readonly && void toggleSerialNumber(serial.serialNumber, false)}
                    />
                </span>
            );

            // After scanning (readonly) the chips are static, so surface who/when it was scanned.
            if (serial.scanBy || serial.scanDt) {
                return (
                    <Tooltip
                        key={serial.serialNumber}
                        placement={'top'}
                        title={t('scanInfoTooltip', {
                            scanBy: serial.scanBy ? getOperatorName(serial.scanBy) : '--',
                            scanDt: serial.scanDt
                                ? DateUtils.serverToLocal(serial.scanDt).format(DateUtils.dateTimeFormat)
                                : '--',
                        })}
                    >
                        {chip}
                    </Tooltip>
                );
            }

            return chip;
        });
    }, [committing, getOperatorName, item?.committedSerials, readonly, t, toggleSerialNumber]);

    /**
     * Prefetches the display names of the operators who scanned the committed serials so
     * the tooltip shows a name instead of an id. Cached in the transfer information view.
     */
    useEffect(() => {
        (item?.committedSerials ?? []).forEach((serial) => {
            if (serial.scanBy) {
                void resolveOperator(serial.scanBy);
            }
        });
    }, [item?.committedSerials, resolveOperator]);

    const qtyChanged = useMemo(
        () => Number(qtyValue ?? 0) !== Number(item?.totalScannedCount ?? 0),
        [item?.totalScannedCount, qtyValue],
    );

    const canSubmitQty = useMemo(() => {
        if (!qtyChanged) {
            return false;
        }
        const parsed = Number(qtyValue);
        return !Number.isNaN(parsed) && parsed >= 0;
    }, [qtyChanged, qtyValue]);

    /**
     * Commits or uncommits a quantity change for non-serialized lines.
     */
    const commitQuantityChanges = useCallback(async () => {
        const currentQty = Number(item?.totalScannedCount ?? 0);
        const nextQty = Number(qtyValue ?? 0);
        const diff = nextQty - currentQty;
        if (!diff) {
            return;
        }
        setQtyCommitting(true);
        const payload = {
            transferId,
            itemId: item.id,
            committedQty: Math.abs(diff),
            serialNumbers: [],
        };
        const response = diff > 0
            ? await Api.commitInventoryTransferItem(payload)
            : await Api.uncommitInventoryTransferItem(payload);
        if (!isMounted()) {
            return;
        }
        setQtyCommitting(false);
        if (response?.resultFlag) {
            applyItemUpdate(response.data);
        }
    }, [applyItemUpdate, isMounted, item.id, item?.totalScannedCount, qtyValue, transferId]);

    if (readonly) {
        if (committedSerialNumbers.length > 0) {
            return <div className={'d-flex w-100 flex-wrap'} style={{gap: 8}}>{serialChips}</div>;
        }
        if (item?.totalScannedCount > 0) {
            return <span {...dataGridCellTextProps}>{item?.totalScannedCount ?? 0}</span>;
        }
        return <span {...dataGridCellTextProps}>--</span>;
    }

    if (mode === 'unknown' || loading) {
        return (
            <div className={'d-flex align-items-center  w-100'} style={{minHeight: 32}}>
                <CircularProgress size={20} color={'secondary'}/>
            </div>
        );
    }

    if (mode === 'serial' || committedSerialNumbers.length > 0) {
        return (
            <div className={'d-flex w-100 flex-wrap align-items-center'} style={{gap: 8}}>
                {serialChips}
                {
                    committedSerialNumbers.length < Number(item?.requestedQty ?? 0) &&
                    <>
                        {
                            committing &&
                            <div className={'loading sales-input'}>
                                <div/>
                            </div>
                        }
                        {
                            !committing &&
                            <OutlinedAutoComplete
                                size={'small'}
                                style={{width: '180px'}}
                                value={autoCompleteValue}
                                loading={loading}
                                onSelect={onSelectNewSerialNumber}
                                onChange={(e) => setAutoCompleteText(e.target.value)}
                                clearOnBlur
                                freeSolo={false}
                                type={'text'}
                                variant={'outlined'}
                                inputValue={autoCompleteText}
                                filterOptions={filterOptions}
                                options={serialOptions}
                                renderOption={renderAutoCompleteSerial}
                                getOptionDisabled={(option) => !option.value?.isAvailable}
                                loadingText={
                                    <div className={'loading auto-complete'}>
                                        <div/>
                                        <div/>
                                        <div/>
                                        <div/>
                                    </div>
                                }
                            />
                        }
                    </>
                }
            </div>
        );
    }

    return (
        <div className={'d-flex align-items-center flex-wrap w-100'} style={{gap: 8}}>
            <OutlinedInput
                fullWidth={false}
                size={'small'}
                type={'number'}
                disabled={qtyCommitting}
                value={qtyValue}
                onChange={setQtyValue}
                onBlur={() => {
                    // enforce min/max on blur
                    if (Number.isNaN(sourceAvailability)) {
                        return;
                    }
                    const min = 0;
                    const max = Math.min(item?.requestedQty ?? 0, sourceAvailability ?? 0);
                    if (qtyValue < min) {
                        setQtyValue(min);
                    } else if (qtyValue > max) {
                        setQtyValue(max);
                    }
                }}
                InputProps={{
                    endAdornment: !Number.isNaN(sourceAvailability) ?
                        <Tooltip title={t('sourceAvailabilityTooltip', {count: sourceAvailability})} placement={'top'}>
                            <span className={'primary-color-1 text-sm'}>/{sourceAvailability}</span>
                        </Tooltip>
                        : null,
                }}
                inputProps={{
                    min: 0,
                    max: Math.min(item?.requestedQty ?? 0, sourceAvailability ?? 0),
                }}
                sx={{
                    minWidth: 100,
                }}
            />
            <SaveOrRollback
                column={false}
                showText={false}
                loading={qtyCommitting}
                show={{
                    save: canSubmitQty,
                    cancel: qtyChanged,
                }}
                onRollback={() => setQtyValue(item?.totalScannedCount ?? 0)}
                onSave={commitQuantityChanges}
            />
        </div>
    );
};

export default TransferItemCommitEditor;
